import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, getToken } from '../api/client';
import { urlImpression } from '../api/config';
import { Badge, Bandeau, Carte, Modale, Squelette, Vide } from '../components/ui';
import { dateFr, montant, nomClient } from '../lib/format';

interface Facture {
  _id: string; numero: string; type: string; date: string; totalMinor: number; devise: string;
  customer?: { nom: string; prenom?: string; raisonSociale?: string };
  contract?: { numero: string };
}
interface ContratLeger {
  _id: string; numero: string; totalGeneralMinor: number; totalEncaisseMinor: number; soldeMinor: number; statut: string;
  customer?: { nom: string; prenom?: string; raisonSociale?: string };
}

export default function Factures() {
  const [liste, setListe] = useState<Facture[] | null>(null);
  const [contrats, setContrats] = useState<ContratLeger[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => {
    api.get<Facture[]>('/factures').then(setListe);
    api.get<ContratLeger[]>('/contrats').then(setContrats);
  }, []);
  useEffect(charger, [charger]);
  if (!liste) return <Squelette lignes={6} />;

  async function emettre(contract: string, type: 'acompte' | 'solde') {
    setErreur('');
    try { const f = await api.post<Facture>('/factures', { contract, type }); setOuvert(false); charger();
      window.open(urlImpression(`/factures/${f._id}/imprimer`, getToken()), '_blank'); }
    catch (e) { setErreur(e instanceof ApiError ? e.erreur : 'Erreur'); }
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Factures</h1>
          <p>Numérotation séquentielle inaltérable — une facture émise n'est jamais modifiée</p>
        </div>
        <div className="actions"><button className="btn btn-p" onClick={() => setOuvert(true)}>Émettre une facture</button></div>
      </div>

      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      <Carte pad={false}>
        {liste.length === 0 ? <div className="carte-p"><Vide titre="Aucune facture émise" /></div> : (
          <table className="tbl">
            <thead><tr><th>N°</th><th>Date</th><th>Type</th><th>Client</th><th>Contrat</th><th className="droite">Total</th><th /></tr></thead>
            <tbody>
              {liste.map((f) => (
                <tr key={f._id}>
                  <td><b>{f.numero}</b></td>
                  <td className="num">{dateFr(f.date)}</td>
                  <td><Badge statut={f.type === 'avoir' ? 'retard' : f.type === 'acompte' ? 'confirmee' : 'en_cours'} libelle={f.type} /></td>
                  <td>{nomClient(f.customer)}</td>
                  <td className="mini sourdine">{f.contract?.numero ?? '—'}</td>
                  <td className="droite montant">{montant(f.totalMinor, f.devise)}</td>
                  <td className="droite">
                    <a className="btn btn-s" target="_blank" rel="noreferrer" href={urlImpression(`/factures/${f._id}/imprimer`, getToken())}>Imprimer</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Carte>

      {ouvert && (
        <Modale titre="Émettre une facture" onFermer={() => setOuvert(false)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            La facture est construite depuis le contrat : location, suppléments du départ, suppléments du retour,
            moins ce qui a déjà été encaisse.
          </p>
          <div style={{ display: 'grid', gap: 8, maxHeight: '55vh', overflowY: 'auto' }}>
            {contrats.map((c) => (
              <div key={c._id} style={{ border: '1px solid var(--bord)', borderRadius: 'var(--r-champ)', padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div>
                    <b>{c.numero}</b> <Badge statut={c.statut} />
                    <div className="mini sourdine">{nomClient(c.customer)} · solde {montant(c.soldeMinor)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-s" disabled={c.totalEncaisseMinor <= 0} onClick={() => void emettre(c._id, 'acompte')}>Acompte</button>
                    <button className="btn btn-s btn-p" onClick={() => void emettre(c._id, 'solde')}>Solde</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modale>
      )}
    </>
  );
}
